import customColorMixin from '@/mixins/customColor';
import moment from 'moment';
import cloneDeep from 'lodash/cloneDeep';
import compact from 'lodash/compact';

export default {
  mixins: [customColorMixin],
  data: function () {
    return {
      isLoadingPacketMetrics: false,
      enableChartAnimations: true,
      packetMetricsData: {
        common: {
          labels: [],
        },
        individual: [
          {
            data: [],
          },
          {
            data: [],
          },
        ],
      },
    };
  },
  computed: {
    packetMetricsOptions() {
      return {
        tooltips: {
          enabled: true,
          callbacks: {
            title: () => {
              return '';
            },
          },
        },
        scales: {
          xAxes: [
            {
              type: 'time',
              distribution: 'series',
              ticks: {
                maxRotation: 0,
              },
              time: {
                displayFormats: {
                  minute: this.language === 'de' ? 'HH:mm' : 'hh:mm A',
                  hour: this.language === 'de' ? 'HH:mm' : 'hh:mm A',
                  shortWeek: this.language === 'de' ? 'dd HH:mm' : 'dd hh:mm A',
                },
              },
            },
          ],
        },
        // https://stackoverflow.com/a/53297548
        animation: {
          duration: this.enableChartAnimations ? 1000 : 0,
        },
        // animation duration after a resize
        responsiveAnimationDuration: this.enableChartAnimations ? 1000 : 0,
      };
    },
    packetsMetricsDataComp() {
      return this.createPacketMetricsDataComp('packetMetricsData');
    },
  },
  methods: {
    createPacketMetricsDataComp(targetBucketName, label) {
      return {
        common: {
          labels: this[targetBucketName].common.labels,
        },
        individual: compact([
          {
            color: this.customColor,
            label: label || this.$i18n_t('DigitalTwin.VirtualDevice.details.latestTimeRange'),
            data: this[targetBucketName].individual[0].data,
            labels: this[targetBucketName].individual[0].labels,
          },
          this[targetBucketName].individual.length > 1
            ? {
                color: '#aaa',
                label: this.$i18n_t('DigitalTwin.VirtualDevice.details.previousTimeRange'),
                data: this[targetBucketName].individual[1].data,
                labels: this[targetBucketName].individual[1].labels,
              }
            : undefined,
          this[targetBucketName].individual.length > 2
            ? {
                color: '#aaa',
                label: this.$i18n_t('NiotaClassic.Devices.table.threshold'),
                data: this[targetBucketName].individual[2].data,
                labels: this[targetBucketName].individual[2].labels,
              }
            : undefined,
        ]),
      };
    },
    // if data loaded from Influx, it uses the END timestamp of an aggregation-window period
    // so shift the data around the average window-period (disregard the latest window),
    // i.e. preferrably it will be the beginning and show the "right"/"human understandable" label as timestamp
    _mapTimeLabels(labels, averageTimeLabels = false) {
      const timestamps = labels.map((v) => moment(v.time).toISOString());

      let avgDuration = 0;
      if (averageTimeLabels && timestamps.length > 2) {
        timestamps.map((t, i) => {
          if (i > 0 && i < timestamps.length - 1) {
            avgDuration += new Date(t) - new Date(timestamps[i - 1]);
          }
          return null;
        });
        avgDuration /= labels.length - 2;
      }
      return timestamps.map((t, i) => {
        if (i < timestamps.length - 1) {
          return new Date(new Date(t) - avgDuration * 0.5).toISOString();
        }
        return new Date(t).toISOString();
      });
    },
    preparePacketMetricsData(
      newestRangeData,
      olderRangeData,
      targetOptionsName,
      targetBucketName,
      averageTimeLabels = false
    ) {
      const refBucket = newestRangeData.length >= olderRangeData.length ? newestRangeData : olderRangeData;

      if (refBucket.length > 0) {
        const oldestPacket = refBucket[0].time;
        const newestPacket = refBucket[refBucket.length - 1].time;
        const { days } = this.diffTimeAndDays(oldestPacket, newestPacket);

        this[targetOptionsName].scales.xAxes.forEach((axis) => {
          axis.time.unit = days >= 3 ? 'day' : undefined;
        });
      }
      this[targetBucketName].common.labels = this._mapTimeLabels(refBucket, averageTimeLabels);
      this[targetBucketName].individual[0].data = newestRangeData.map((v) => v.count);
      this[targetBucketName].individual[0].labels = this[targetBucketName].common.labels;
      if (this[targetBucketName].individual.length > 1) {
        this[targetBucketName].individual[1].data = olderRangeData.map((v) => v.count);
        this[targetBucketName].individual[1].labels = this[targetBucketName].common.labels;
      }
      if (this[targetBucketName].individual.length > 2) {
        this[targetBucketName].individual[2].labels = this[targetBucketName].common.labels;
      }

      if (this[targetBucketName].common.labels.length === 0) {
        this[targetBucketName].common.labels = [new Date().toISOString()];
        this[targetBucketName].individual[0].data = [0];
        this[targetBucketName].individual[0].labels = this[targetBucketName].common.labels;
      }

      this[targetBucketName] = cloneDeep(this[targetBucketName]);
    },
    diffTimeAndDays(time1, time2) {
      let date1 = new Date(time1);
      let date2 = new Date(time2);
      // Milliseconds
      const milliseconds = Math.abs(date2 - date1);
      const mins = Math.floor(milliseconds / 60000);
      const days = Math.ceil(milliseconds / (1000 * 60 * 60 * 24));
      return { milliseconds, mins, days };
    },
  },
};
